import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck} from '../../helpers'
import {AuthorizedApiBase} from '../../api/api-client-config'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

class EdmApiClient extends AuthorizedApiBase {
  constructor(baseUrl) {
    super()
    this.baseUrl = baseUrl
    this.http = window
  }

  fetchInvoices(startDate, endDate) {
    const url = `${this.baseUrl}/api/v1/edm/fetch?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`
    const options = {method: 'GET', headers: {Accept: 'application/json'}}
    return this.transformOptions(options)
      .then((opts) => this.http.fetch(url, opts))
      .then((response) => {
        if (response.status === 200) return response.json()
        return response.text().then((text) => { throw new Error(text || 'Fatura listesi alınamadı') })
      })
  }

  importInvoices(body) {
    const url = `${this.baseUrl}/api/v1/edm/import`
    const options = {
      method: 'POST',
      headers: {'Accept': 'application/json', 'Content-Type': 'application/json'},
      body: JSON.stringify(body),
    }
    return this.transformOptions(options)
      .then((opts) => this.http.fetch(url, opts))
      .then((response) => {
        if (response.status === 200) return response.json()
        return response.text().then((text) => { throw new Error(text || 'Aktarım sırasında hata oluştu') })
      })
  }

  listSessions() {
    const url = `${this.baseUrl}/api/v1/edm/sessions`
    const options = {method: 'GET', headers: {Accept: 'application/json'}}
    return this.transformOptions(options)
      .then((opts) => this.http.fetch(url, opts))
      .then((response) => {
        if (response.status === 200) return response.json()
        return response.text().then((text) => { throw new Error(text || 'Aktarım geçmişi alınamadı') })
      })
  }

  getSession(id) {
    const url = `${this.baseUrl}/api/v1/edm/session/${encodeURIComponent(id)}`
    const options = {method: 'GET', headers: {Accept: 'application/json'}}
    return this.transformOptions(options)
      .then((opts) => this.http.fetch(url, opts))
      .then((response) => {
        if (response.status === 200) return response.json()
        return response.text().then((text) => { throw new Error(text || 'Oturum detayı alınamadı') })
      })
  }

  getPdf(uuid) {
    const url = `${this.baseUrl}/api/v1/edm/pdf/${encodeURIComponent(uuid)}`
    const options = {method: 'GET', headers: {Accept: 'application/octet-stream'}}
    return this.transformOptions(options)
      .then((opts) => this.http.fetch(url, opts))
      .then((response) => {
        if (response.status === 200 || response.status === 206) {
          const cd = response.headers?.get('content-disposition')
          const m = cd ? /filename="?([^"]*?)"?(;|$)/g.exec(cd) : null
          const fileName = m?.[1]
          return response.blob().then((blob) => ({fileName, data: blob, status: response.status}))
        }
        return response.text().then((text) => { throw new Error(text || 'PDF alınamadı') })
      })
  }
}

const api = new EdmApiClient(SERVICE_URL)

const initialState = {
  loading: false,
  fetchLoading: false,
  importLoading: false,
  sessionDetailLoading: false,
  error: null,
  invoiceList: null,
  importResult: null,
  sessionList: null,
  sessionDetail: null,
  pdfData: null,
}

export const edmFetchInvoices = createAsyncThunk(
  'edminvoice/fetch',
  async (params, {dispatch}) => {
    try {
      dispatch(setFetchLoading(true))
      const response = await api.fetchInvoices(params.startDate, params.endDate)
      dispatch(setInvoiceList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error?.message || 'Fatura listesi alınırken hata oluştu'))
      throw error
    }
  }
)

export const edmImportInvoices = createAsyncThunk(
  'edminvoice/import',
  async (body, {dispatch}) => {
    try {
      dispatch(setImportLoading(true))
      const response = await api.importInvoices(body)
      dispatch(setImportResult(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error?.message || 'Aktarım sırasında hata oluştu'))
      throw error
    }
  }
)

export const edmListSessions = createAsyncThunk(
  'edminvoice/listSessions',
  async (_, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await api.listSessions()
      dispatch(setSessionList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error?.message || 'Aktarım geçmişi alınırken hata oluştu'))
      throw error
    }
  }
)

export const edmGetSession = createAsyncThunk(
  'edminvoice/getSession',
  async (id, {dispatch}) => {
    try {
      dispatch(setSessionDetailLoading(true))
      const response = await api.getSession(id)
      dispatch(setSessionDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setSessionDetailLoading(false))
      dispatch(setError(error?.message || 'Oturum detayı alınırken hata oluştu'))
      throw error
    }
  }
)

export const edmGetPdf = createAsyncThunk(
  'edminvoice/getPdf',
  async (uuid, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await api.getPdf(uuid)
      dispatch(setPdfData(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error?.message || 'PDF alınırken hata oluştu'))
      throw error
    }
  }
)

const edminvoiceSlice = createSlice({
  name: 'edminvoice',
  initialState,
  reducers: {
    setLoading: (state, action) => { state.loading = action.payload },
    setFetchLoading: (state, action) => { state.fetchLoading = action.payload },
    setImportLoading: (state, action) => { state.importLoading = action.payload },
    setSessionDetailLoading: (state, action) => { state.sessionDetailLoading = action.payload },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
      state.fetchLoading = false
      state.importLoading = false
    },
    setInvoiceList: (state, action) => {
      state.invoiceList = action.payload
      state.fetchLoading = false
    },
    setImportResult: (state, action) => {
      state.importResult = action.payload
      state.importLoading = false
    },
    setSessionList: (state, action) => {
      state.sessionList = action.payload
      state.loading = false
    },
    setSessionDetail: (state, action) => {
      state.sessionDetail = action.payload
      state.sessionDetailLoading = false
    },
    setPdfData: (state, action) => {
      state.pdfData = action.payload
      state.loading = false
    },
    clearError: (state) => { state.error = null },
    resetState: (state) => { Object.assign(state, initialState) },
  },
})

export const {
  setLoading,
  setFetchLoading,
  setImportLoading,
  setSessionDetailLoading,
  setError,
  setInvoiceList,
  setImportResult,
  setSessionList,
  setSessionDetail,
  setPdfData,
  clearError,
  resetState,
} = edminvoiceSlice.actions

export const selectEdmInvoiceList = (state) => state.edminvoice.invoiceList
export const selectEdmImportResult = (state) => state.edminvoice.importResult
export const selectEdmFetchLoading = (state) => state.edminvoice.fetchLoading
export const selectEdmImportLoading = (state) => state.edminvoice.importLoading
export const selectEdmError = (state) => state.edminvoice.error
export const selectEdmSessionList = (state) => state.edminvoice.sessionList
export const selectEdmSessionDetail = (state) => state.edminvoice.sessionDetail
export const selectEdmSessionDetailLoading = (state) => state.edminvoice.sessionDetailLoading
export const selectEdmPdfData = (state) => state.edminvoice.pdfData
export const selectEdmLoading = (state) => state.edminvoice.loading

export default edminvoiceSlice.reducer
